import React, { useMemo } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  BusinessTemplateType,
  CustomSettings,
  isProformaInvoiceTemplateType,
} from "subModule/src/services/invoice/types";
import { useDefaultLabels } from "../hooks/useDefaultLabels";
import {
  getThermalBaseFontSize,
  getThermalPaperWidthMm,
  normalizeThermalSectionOrder,
} from "./thermalInvoiceUtils";

export interface ThermalInvoiceLineItem {
  type?: string;
  title?: string;
  code?: string;
  description?: string;
  quantity?: number;
  unitPrice?: number;
  discount?: number;
  tax?: number;
  amount?: number;
}

export interface ThermalDeliveryNote {
  vehicleName?: string;
  vehiclePlateNo?: string;
  deliveryNoteNo?: string;
  subtotal?: number;
  discount?: number;
  tax?: number;
  total?: number;
}

export interface ThermalInvoiceData {
  invoiceNo: string;
  date: string;
  company: Record<string, string>;
  customer: Record<string, string>;
  vehicle?: Record<string, string>;
  items?: ThermalInvoiceLineItem[];
  deliveryNotes?: ThermalDeliveryNote[];
  subtotal?: number;
  discount?: number;
  tax?: number;
  total?: number;
  creditAmount?: number;
  paidAmount?: number;
  dueAmount?: number;
  paymentMode?: string;
  footerText?: string;
  termsText?: string;
  notesText?: string;
  logoUrl?: string;
  isConsolidated?: boolean;
}

interface ThermalInvoiceContentProps {
  settings: CustomSettings;
  data: ThermalInvoiceData;
  theme?: { primary?: string };
  formatNumber?: (value: number) => string;
  currencySymbol?: string;
  templateType?: number;
}

/** Placeholder QR tile (POS should render real QR from invoice URL / payload) */
const ThermalQrPlaceholder: React.FC<{ size?: number; smallFont: number }> = ({
  size = 48,
  smallFont,
}) => {
  const { t } = useTranslation();
  const qrPattern = [
    1, 0, 1, 0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 1, 0, 1, 1, 0, 1, 0, 1, 0, 1, 0,
    0, 1, 0, 1, 0, 1, 0, 1, 1, 0, 1, 0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 1, 0, 1,
    1, 0, 1, 0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 1, 0, 1,
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.25,
      }}
    >
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ fontSize: `${smallFont}px` }}
      >
        {t("qrCodeSection")}
      </Typography>
      <Box
        sx={{
          width: size,
          height: size,
          border: "1px solid #999",
          display: "grid",
          gridTemplateColumns: "repeat(8, 1fr)",
          gridTemplateRows: "repeat(8, 1fr)",
          gap: "1px",
          p: 0.25,
          backgroundColor: "#f5f5f5",
        }}
      >
        {qrPattern.map((value, i) => (
          <Box
            key={i}
            sx={{
              backgroundColor: value === 1 ? "#000" : "#fff",
              width: "100%",
              height: "100%",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const ThermalInvoiceContent: React.FC<ThermalInvoiceContentProps> = ({
  settings,
  data,
  theme,
  formatNumber = (n) => Number(n || 0).toFixed(2),
  currencySymbol = "",
  templateType = BusinessTemplateType.INVOICE,
}) => {
  const isProforma = isProformaInvoiceTemplateType(templateType);
  const { t } = useTranslation();
  const {
    getTableColumnLabel,
    getConsolidatedTableColumnLabel,
    getSummaryLabel,
    getPaymentLabel,
  } = useDefaultLabels();

  const widthMm = getThermalPaperWidthMm(settings);
  const baseFont = getThermalBaseFontSize(widthMm);
  const smallFont = baseFont - 1;
  const headingFont = baseFont + 2;
  const primaryColor = theme?.primary || "#fc5f3a";

  const sectionOrder = useMemo(
    () => normalizeThermalSectionOrder(settings.sectionOrder),
    [settings.sectionOrder],
  );

  const renderLanguage = (primary: string, secondary?: string) => {
    if (settings.languageDisplay === "primary") return primary;
    if (settings.languageDisplay === "secondary") return secondary || primary;
    if (!secondary) return primary;
    return (
      <>
        <span>{primary}</span>
        {secondary && (
          <>
            <br />
            <span className="ff-arabic" style={{ fontSize: `${smallFont}px` }}>
              {secondary}
            </span>
          </>
        )}
      </>
    );
  };

  const formatDate = (date: string) =>
    dayjs(date).format(settings.dateFormat || "DD-MM-YYYY");

  const getFieldLabel = (
    field: string,
    type: "company" | "customer" | "vehicle",
  ) => {
    const labels =
      type === "company"
        ? settings.companyLabels?.[
            field as keyof typeof settings.companyLabels
          ]
        : type === "customer"
          ? settings.customerLabels?.[
              field as keyof typeof settings.customerLabels
            ]
          : settings.vehicleLabels?.[
              field as keyof typeof settings.vehicleLabels
            ];
    return {
      primary: labels?.primary?.trim() || "",
      secondary: labels?.secondary?.trim() || "",
    };
  };

  const renderDetailRow = (
    field: string,
    value: string,
    type: "company" | "customer",
  ) => {
    if (!value) return null;
    const label = getFieldLabel(field, type);
    return (
      <Box
        key={`${type}-${field}`}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 0.5,
          py: 0.25,
          fontSize: `${baseFont}px`,
          wordBreak: "break-word",
        }}
      >
        <Box sx={{ flex: "0 0 42%", fontWeight: 600 }}>
          {renderLanguage(label.primary, label.secondary)}
        </Box>
        <Box sx={{ flex: 1, textAlign: "right" }}>{value}</Box>
      </Box>
    );
  };

  const renderSectionTitle = (primary: string, secondary?: string) => (
    <Typography
      sx={{
        fontSize: `${headingFont}px`,
        fontWeight: 700,
        color: primaryColor,
        textAlign: "center",
        my: 0.5,
      }}
    >
      {renderLanguage(primary, secondary)}
    </Typography>
  );

  const renderStackedFields = (
    fieldsConfig: Record<string, boolean | undefined> | undefined,
    values: Record<string, string>,
    type: "company" | "customer",
  ) =>
    Object.entries(fieldsConfig || {})
      .filter(([, enabled]) => enabled)
      .map(([field]) => renderDetailRow(field, values[field] || "", type));

  const renderCurrency = (amount: number) =>
    `${currencySymbol}${formatNumber(amount)}`.trim();

  const itemOptions = settings.itemDisplayOptions || {
    showTitle: true,
    showCode: true,
    showDescription: false,
  };

  const isConsolidated =
    data.isConsolidated || settings.invoiceType === "consolidated";

  const renderLineItems = () => {
    if (isConsolidated && data.deliveryNotes?.length) {
      return data.deliveryNotes.map((note, index) => (
        <Box
          key={`dn-${index}`}
          sx={{
            borderBottom: "1px dashed #ccc",
            py: 0.5,
            fontSize: `${baseFont}px`,
          }}
        >
          <Box sx={{ fontWeight: 600 }}>
            #{index + 1}{" "}
            {note.vehicleName || note.deliveryNoteNo || t("deliveryNote")}
          </Box>
          {note.vehiclePlateNo && (
            <Box sx={{ fontSize: `${smallFont}px` }}>{note.vehiclePlateNo}</Box>
          )}
          {settings.consolidatedTableColumns?.total !== false && (
            <Box sx={{ textAlign: "right", fontWeight: 600 }}>
              {renderCurrency(note.total || 0)}
            </Box>
          )}
        </Box>
      ));
    }

    const items = (data.items || []).filter((item) => {
      if (item.type === "package" && settings.showPackages === false)
        return false;
      if (item.type === "service" && settings.showServices === false)
        return false;
      if (item.type === "item" && settings.showItems === false) return false;
      return true;
    });

    return items.map((item, index) => {
      const parts: string[] = [];
      if (itemOptions.showTitle && item.title) parts.push(item.title);
      if (itemOptions.showCode && item.code && item.code !== item.title)
        parts.push(`(${item.code})`);
      const title = parts.join(" ") || t("item");

      const showQty = settings.tableColumns?.quantity !== false;
      const showPrice = settings.tableColumns?.unitPrice !== false;
      const showAmount = settings.tableColumns?.amount !== false;

      return (
        <Box
          key={`item-${index}`}
          sx={{
            borderBottom: "1px dashed #ccc",
            py: 0.5,
            fontSize: `${baseFont}px`,
          }}
        >
          <Box sx={{ fontWeight: 600 }}>
            {settings.tableColumns?.serialNo !== false ? `${index + 1}. ` : ""}
            {title}
          </Box>
          {itemOptions.showDescription && item.description && (
            <Box sx={{ fontSize: `${smallFont}px`, color: "#555" }}>
              {item.description}
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: `${smallFont}px`,
              mt: 0.25,
            }}
          >
            <span>
              {showQty && `${item.quantity ?? 1} x `}
              {showPrice && renderCurrency(item.unitPrice || 0)}
            </span>
            {showAmount && (
              <span style={{ fontWeight: 600 }}>
                {renderCurrency(item.amount || 0)}
              </span>
            )}
          </Box>
          {settings.tableColumns?.tax !== false && (item.tax || 0) > 0 && (
            <Box sx={{ fontSize: `${smallFont}px`, textAlign: "right" }}>
              {renderLanguage(
                getTableColumnLabel("tax").primary,
                getTableColumnLabel("tax").secondary,
              )}
              : {renderCurrency(item.tax || 0)}
            </Box>
          )}
        </Box>
      );
    });
  };

  const renderSummaryRow = (
    key: keyof NonNullable<CustomSettings["summaryItems"]>,
    value: number,
  ) => {
    if (settings.summaryItems?.[key] === false) return null;
    const label = getSummaryLabel(key);
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          py: 0.25,
          fontSize: `${baseFont}px`,
          fontWeight: key === "total" ? 700 : 400,
        }}
      >
        <span>{renderLanguage(label.primary, label.secondary)}</span>
        <span>{renderCurrency(value)}</span>
      </Box>
    );
  };

  const renderLogoBanner = () => {
    const logoPosition =
      settings.logoPosition || (settings.showLogo !== false ? "left" : "none");
    const numberPositionField = isProforma
      ? settings.proformaInvoiceNumberPosition
      : settings.invoiceNumberPosition;
    const showNumberField = isProforma
      ? settings.showProformaInvoiceNumber
      : settings.showInvoiceNumber;
    const invoiceNumberPosition =
      numberPositionField || (showNumberField !== false ? "center" : "none");

    const showLogo = settings.showLogo !== false && logoPosition !== "none";
    const showInvoiceNumber =
      showNumberField !== false && invoiceNumberPosition !== "none";

    if (!showLogo && !showInvoiceNumber) return null;

    const fontSize = isProforma
      ? settings.proformaInvoiceNumberFontSize || headingFont
      : settings.invoiceNumberFontSize || headingFont;
    const fontWeight = isProforma
      ? settings.proformaInvoiceNumberFontWeight || 700
      : settings.invoiceNumberFontWeight || 700;
    const headingPrimary = isProforma
      ? settings.proformaInvoiceHeadingPrimary ||
        settings.invoiceHeadingPrimary ||
        "PROFORMA INVOICE"
      : settings.invoiceHeadingPrimary || "INVOICE";
    const headingSecondary = isProforma
      ? settings.proformaInvoiceHeadingSecondary ||
        settings.invoiceHeadingSecondary
      : settings.invoiceHeadingSecondary;
    const isColumnDirection = settings.showInvoiceDirection === "column";

    const fitToWidth =
      !settings.useDefaultLogoSize && !!settings.logoFitToWidth;
    const lw = settings.useDefaultLogoSize
      ? Math.min(80, widthMm * 4)
      : fitToWidth
        ? "100%"
        : settings.logoWidth || 56;
    const lh = settings.useDefaultLogoSize
      ? Math.min(44, widthMm * 2.2)
      : settings.logoHeight || 36;

    const renderLogoGraphic = () => {
      if (!showLogo) return null;
      return data.logoUrl ? (
        <Box
          component="img"
          src={data.logoUrl}
          alt="logo"
          sx={{
            width: fitToWidth ? "100%" : undefined,
            maxWidth: fitToWidth ? "100%" : lw,
            maxHeight: lh,
            height: fitToWidth ? "auto" : undefined,
            objectFit: "contain",
          }}
        />
      ) : (
        <Box
          sx={{
            width: lw,
            maxWidth: "100%",
            height: lh,
            border: "1px dashed #ccc",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: `${smallFont}px`,
            color: "#888",
            flexShrink: 0,
          }}
        >
          {t("logo")}
        </Box>
      );
    };

    const renderNumberBlock = () => {
      if (!showInvoiceNumber) return null;
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "inherit",
            flex: isColumnDirection ? "1 1 auto" : "0 0 auto",
            maxWidth: "100%",
          }}
        >
          <Typography
            sx={{
              fontSize: `${fontSize}px`,
              fontWeight,
              color: primaryColor,
              lineHeight: 1.2,
            }}
          >
            {headingPrimary}
          </Typography>
          {headingSecondary && (
            <Typography
              className="ff-arabic"
              sx={{ fontSize: `${smallFont}px`, color: primaryColor }}
            >
              {headingSecondary}
            </Typography>
          )}
          <Typography sx={{ fontSize: `${baseFont}px`, fontWeight: 600 }}>
            {t("invoiceNo")}: {data.invoiceNo}
          </Typography>
          <Typography sx={{ fontSize: `${smallFont}px`, fontWeight: 500 }}>
            {t("date")}: {formatDate(data.date)}
          </Typography>
        </Box>
      );
    };

    if (isColumnDirection) {
      return (
        <Box sx={{ mb: 1, width: "100%" }}>
          {showLogo && (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: fitToWidth
                  ? "stretch"
                  : logoPosition === "left"
                    ? "flex-start"
                    : logoPosition === "center"
                      ? "center"
                      : "flex-end",
                mb: showInvoiceNumber ? 0.75 : 0,
              }}
            >
              {fitToWidth ? (
                <Box sx={{ width: "100%" }}>{renderLogoGraphic()}</Box>
              ) : (
                renderLogoGraphic()
              )}
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems:
                invoiceNumberPosition === "left"
                  ? "flex-start"
                  : invoiceNumberPosition === "right"
                    ? "flex-end"
                    : "center",
              textAlign:
                invoiceNumberPosition === "left"
                  ? "left"
                  : invoiceNumberPosition === "right"
                    ? "right"
                    : "center",
            }}
          >
            {renderNumberBlock()}
          </Box>
        </Box>
      );
    }

    const leftItems: React.ReactNode[] = [];
    const centerItems: React.ReactNode[] = [];
    const rightItems: React.ReactNode[] = [];

    if (showLogo && !fitToWidth) {
      const el = <Box key="tl">{renderLogoGraphic()}</Box>;
      if (logoPosition === "left") leftItems.push(el);
      else if (logoPosition === "center") centerItems.push(el);
      else if (logoPosition === "right") rightItems.push(el);
    }
    if (showInvoiceNumber) {
      const el = <Box key="tn">{renderNumberBlock()}</Box>;
      if (invoiceNumberPosition === "left") leftItems.push(el);
      else if (invoiceNumberPosition === "center") centerItems.push(el);
      else if (invoiceNumberPosition === "right") rightItems.push(el);
    }

    return (
      <Box sx={{ mb: 1, width: "100%" }}>
        {fitToWidth && showLogo ? (
          <Box sx={{ width: "100%", mb: 0.75 }}>{renderLogoGraphic()}</Box>
        ) : null}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.5,
              flex: "1 1 0",
            }}
          >
            {leftItems}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              flex: "1 1 0",
            }}
          >
            {centerItems}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.5,
              flex: "1 1 0",
            }}
          >
            {rightItems}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderQrSection = () => {
    if (!settings.showQR) return null;
    const qrSize =
      widthMm <= 58 ? 40 : widthMm <= 80 ? 46 : 52;
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
        <ThermalQrPlaceholder size={qrSize} smallFont={smallFont} />
      </Box>
    );
  };

  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case "logo":
        return (
          <React.Fragment key="logo">
            {renderLogoBanner()}
            <Divider sx={{ my: 0.5 }} />
          </React.Fragment>
        );
      case "header": {
        if (settings.showHeader === false) return null;
        const showCo = settings.showCompanySection !== false;
        const showCu = settings.showCustomerSection !== false;
        if (!showCo && !showCu) return null;
        return (
          <React.Fragment key="header">
            {showCo && (
              <Box sx={{ mb: 0.5 }}>
                {renderSectionTitle(t("companyDetails") || "Company")}
                {renderStackedFields(settings.companyFields, data.company, "company")}
              </Box>
            )}
            {showCu && (
              <Box sx={{ mb: 0.5 }}>
                {renderSectionTitle(t("customerDetails") || "Customer")}
                {renderStackedFields(
                  settings.customerFields,
                  data.customer,
                  "customer",
                )}
              </Box>
            )}
            <Divider sx={{ my: 0.5 }} />
          </React.Fragment>
        );
      }
      case "table": {
        if (settings.showTable === false) return null;
        return (
          <Box key="table" sx={{ mb: 0.5 }}>
            {renderSectionTitle(
              isConsolidated
                ? getConsolidatedTableColumnLabel("deliveryNoteNo").primary
                : t("items") || "Items",
              isConsolidated
                ? getConsolidatedTableColumnLabel("deliveryNoteNo").secondary
                : undefined,
            )}
            {renderLineItems()}
            <Divider sx={{ my: 0.5 }} />
          </Box>
        );
      }
      case "summary":
        return (
          <React.Fragment key="summary">
            <Box sx={{ mb: 0.5 }}>
              {renderSummaryRow("subtotal", data.subtotal || 0)}
              {renderSummaryRow("discount", data.discount || 0)}
              {renderSummaryRow(
                "discountedSubtotal",
                (data.subtotal || 0) - (data.discount || 0),
              )}
              {renderSummaryRow("tax", data.tax || 0)}
              {renderSummaryRow("total", data.total || 0)}
            </Box>
            {settings.showPaymentInfo !== false && (
              <Box sx={{ mb: 0.5, fontSize: `${smallFont}px` }}>
                <Box sx={{ fontWeight: 600, textAlign: "center", mb: 0.25 }}>
                  {renderLanguage(
                    getPaymentLabel().primary,
                    getPaymentLabel().secondary,
                  )}
                </Box>
                {data.paymentMode && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{t("paymentMode")}</span>
                    <span>{data.paymentMode}</span>
                  </Box>
                )}
                {(data.paidAmount || 0) > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <span>
                      {renderLanguage(
                        getSummaryLabel("paidAmount").primary,
                        getSummaryLabel("paidAmount").secondary,
                      )}
                    </span>
                    <span>{renderCurrency(data.paidAmount || 0)}</span>
                  </Box>
                )}
                {(data.dueAmount || 0) > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <span>
                      {renderLanguage(
                        getSummaryLabel("dueAmount").primary,
                        getSummaryLabel("dueAmount").secondary,
                      )}
                    </span>
                    <span>{renderCurrency(data.dueAmount || 0)}</span>
                  </Box>
                )}
              </Box>
            )}
            <Divider sx={{ my: 0.5 }} />
          </React.Fragment>
        );
      case "terms":
        if (settings.showTerms === false) return null;
        return (
          <Box
            key="terms"
            sx={{ mb: 0.5, fontSize: `${smallFont}px`, textAlign: "center" }}
          >
            <Box sx={{ fontWeight: 600 }}>{t("termsAndConditions")}</Box>
            <Box>{data.termsText || t("none")}</Box>
          </Box>
        );
      case "notes":
        if (settings.showNotes === false) return null;
        return (
          <Box
            key="notes"
            sx={{ mb: 0.5, fontSize: `${smallFont}px`, textAlign: "center" }}
          >
            <Box sx={{ fontWeight: 600 }}>{t("notesForCustomer")}</Box>
            <Box>{data.notesText || t("none")}</Box>
          </Box>
        );
      case "qr":
        return <React.Fragment key="qr">{renderQrSection()}</React.Fragment>;
      case "footer":
        if (settings.showFooter === false) return null;
        return (
          <Box
            key="footer"
            sx={{
              fontSize: `${smallFont}px`,
              textAlign: "center",
              borderTop: "1px dashed #000",
              pt: 0.5,
              mt: 0.5,
            }}
          >
            {data.footerText || settings.footerText}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      className="thermal-invoice-receipt"
      sx={{
        width: `${widthMm}mm`,
        maxWidth: "100%",
        margin: "0 auto",
        p: 1,
        backgroundColor: "#fff",
        fontFamily: "Saira, sans-serif",
        color: "#000",
        "@media print": {
          width: `${widthMm}mm`,
          margin: 0,
          p: "2mm",
        },
      }}
    >
      {sectionOrder.map((id) => renderSection(id))}
    </Box>
  );
};

export default ThermalInvoiceContent;
